import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Form, Checkbox, Icon, Popconfirm } from 'antd';
import { autobind } from 'core-decorators';
import moment from 'moment';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import Calendar from 'components-desktop/Calendar';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormTimePicker from 'components-desktop/FormTimePicker';
import FormLink from 'components-desktop/FormLink';
import FormInput from 'components-desktop/FormInput';
import FormInputNumber from 'components-desktop/FormInputNumber';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import FormTextArea from 'components-desktop/FormTextArea';
import ChangeHistoriesButton from 'components-desktop/histories/ChangeHistoriesButton';
import misc from 'misc/misc';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,

  contracts: PropTypes.array.isRequired,
  servicesByCompany: PropTypes.object.isRequired,
};

@autobind
class PatientCalendar extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      adding: false,
      editingVisit: null,

      caregivers: [],
      servicesForAdd: [],

      isSaveButtonEnable: true,
      isDeleteButtonEnable: true,
      isMissedChecked: false,
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getOtherLists();
  }

  /* PUBLIC */
  refresh() {
    this.calendar.refresh();
  }

  async getOtherLists() {
    const caregivers = await this.callServiceForObjects('/caregiver/get_id_names');

    this.setState({
      caregivers,
    });
  }

  getPatientVisits(fromDateString, toDateString) {
    const params = {
      patientId: this.id,
      fromDate: misc.toDateString(moment(fromDateString, 'MM/DD/YYYY')),
      toDate: misc.toDateString(moment(toDateString, 'MM/DD/YYYY')),
    };
    return this.callServiceForRemoteObjects('/schedule_visit/get_list', params);
  }

  onAddClick() {
    this.setState({
      adding: true,
      editingVisit: null,
      isMissedChecked: false,
    });
  }

  onVisitEventClick(event) {
    const visit = event.data.tags.scheduleVisit;

    this.setState({
      adding: false,
      editingVisit: visit,
      isMissedChecked: visit.getBoolean('missed'),
    });
  }

  onCancelClick() {
    this.setState({
      adding: false,
      editingVisit: null,
      isMissedChecked: false,
      servicesForAdd: [],
    });
  }

  async onDeleteClick(visit) {
    try {
      this.setState({
        isDeleteButtonEnable: false,
      });

      const params = { id: visit.getId() };
      await this.callService('/schedule_visit/delete', params);

      this.notifySuccess('Success', 'The schedule visit has been deleted.');

      this.calendar.refresh();
      this.onCancelClick();
    } finally {
      this.setState({
        isDeleteButtonEnable: true,
      });
    }
  }

  async onContractSelect(contractId) {
    const { contracts, servicesByCompany } = this.props;

    const contract = contracts.find(c => c.getId() === contractId);
    this.setState({
      servicesForAdd: (contract && servicesByCompany[contract.getLong('companyId')]) || [],
    }, () => {
      this.props.form.setFieldsValue({
        serviceId: contract?.getLong('serviceId'),
      });
    });
  }

  onMissedCheckboxChange(e) {
    this.setState({
      isMissedChecked: e.target.checked,
    });

    if (e.target.checked) {
      // Clear visit in & visit out.
      this.props.form.setFieldsValue({
        visitIn: null,
        visitOut: null,
      });
    }
  }

  onFromTimeChange(fromTime) {
    const toTime = this.props.form.getFieldValue('toTime');
    this.calcAndSetBillHours(fromTime, toTime);
  }

  onToTimeChange(toTime) {
    const fromTime = this.props.form.getFieldValue('fromTime');
    this.calcAndSetBillHours(fromTime, toTime);
  }

  // Calculates the duration between two time. Note that, when the toTime <= fromTime,
  // we assume that the toTime has gone to the next day.
  calcAndSetBillHours(fromTime, toTime) {
    if (fromTime && toTime) {
      const diffInMs = fromTime.isBefore(toTime)
        ? toTime.diff(fromTime)
        : 86400000 - (fromTime.diff(toTime));

      const duration = moment.duration(diffInMs);
      const hours = Math.floor(duration.asHours());
      const minutes = moment.utc(duration.asMilliseconds()).format('mm');
      const billHours = `${hours < 10 ? '0' : ''}${hours}:${minutes}`;

      this.props.form.setFieldsValue({
        billHours,
      });
    }
  }

  async saveVisit(values) {
    const { adding, editingVisit } = this.state;

    try {
      this.setState({
        isSaveButtonEnable: false,
      });

      if (!values.caregiverId) {
        // eslint-disable-next-line
        if (!confirm('You have not set a caregiver, do you still want to save?')) {
          return;
        }
      }

      if (adding) {
        const params = {
          patientId: this.id,
          date: helpers.extraFormDateValue(values, 'date'),
          contractId: helpers.extraFormSelectValue(values, 'contractId'),
          serviceId: helpers.extraFormSelectValue(values, 'serviceId'),
          disciplineRate: helpers.extraFormInputNumberValue(values, 'disciplineRate'),
          fromTime: helpers.extraFormTimeValue(values, 'fromTime'),
          toTime: helpers.extraFormTimeValue(values, 'toTime'),
          billHours: helpers.extraFormInputValue(values, 'billHours'),
          caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
          visitIn: helpers.extraFormTimeValue(values, 'visitIn'),
          visitOut: helpers.extraFormTimeValue(values, 'visitOut'),
          remark: helpers.extraFormTextAreaValue(values, 'remark'),
        };
        await this.callService('/schedule_visit/add', params);

        this.notifySuccess('Success', 'The schedule visit has been added.');
      } else {
        const invoiced = editingVisit.getLong('invoiceId') > 0;
        const params = {
          id: editingVisit.getId(),
          disciplineRate: helpers.extraFormInputNumberValue(values, 'disciplineRate'),
          caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
          remark: helpers.extraFormTextAreaValue(values, 'remark'),

          // These fields can be changed if not yet invoiced.
          missed: !invoiced ? values.missed : undefined,
          fromTime: !invoiced ? helpers.extraFormTimeValue(values, 'fromTime') : undefined,
          toTime: !invoiced ? helpers.extraFormTimeValue(values, 'toTime') : undefined,
          billHours: !invoiced ? helpers.extraFormInputValue(values, 'billHours') : undefined,
          visitIn: !invoiced ? helpers.extraFormTimeValue(values, 'visitIn') : undefined,
          visitOut: !invoiced ? helpers.extraFormTimeValue(values, 'visitOut') : undefined,
        };

        // If missed, make sure visitIn & visitOut are empty.
        if (values.missed) {
          params.visitIn = '';
          params.visitOut = '';
        }

        await this.callService('/schedule_visit/update', params);

        this.notifySuccess('Success', 'The schedule visit has been updated.');
      }

      this.calendar.refresh();
      this.onCancelClick();
    } finally {
      this.setState({
        isSaveButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.saveVisit(values);
      }
    });
  }

  renderAddEditForm() {
    const { form, contracts } = this.props;
    const {
      adding,
      editingVisit,
      servicesForAdd,
      caregivers,
      isSaveButtonEnable,
      isDeleteButtonEnable,
      isMissedChecked,
    } = this.state;

    const { getFieldDecorator } = form;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        margin="20px 0 8px 0"
        alignItems="center"
        boxShadow="0 1px 6px rgba(0,0,0,.4)"
      >
        <styledComp.AntdForm
          width="match_parent"
          height="wrap_content"
          onSubmit={this.handleSubmit}
        >
          {/* Content */}
          <FlexColContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
          >
            <styledComp.Span
              width="wrap_content"
              height="wrap_content"
              margin="16px 0 32px 0"
              color="textPrimaryDark"
              fontSize="24px"
              fontWeight="300"
            >
              {adding ? 'Add New Schedule Visit' : `Edit Schedule Visit - ${editingVisit.getId()}`}
            </styledComp.Span>

            <Row type="flex" gutter={24}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormDatePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="date"
                  label="Scheduled Date"
                  placeholder="mm/dd/yyyy"
                  disabled={!!editingVisit}
                  extra="Select date from calendar"
                  initialValue={editingVisit?.getMoment('date')}
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormSelect
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="contractId"
                  label="Bill to Contract"
                  placeholder="Select..."
                  disabled={!!editingVisit}
                  options={contracts.map(c => ({
                    id: c.getId(),
                    name: c.getString('fullName'),
                  }))}
                  initialValue={editingVisit?.getLong('contractId')}
                  isRequired
                  onSelect={this.onContractSelect}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                {adding &&
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="serviceId"
                    label="Service Code"
                    placeholder="Select..."
                    allowClear={false}
                    options={servicesForAdd.map(s => ({
                      id: s.id,
                      name: s.displayCode,
                    }))}
                    isRequired
                  />
                }

                {editingVisit &&
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="serviceId"
                    label="Service Code"
                    placeholder=""
                    max={120}
                    disabled
                    initialValue={editingVisit?.getRemoteObject('service')?.getString('displayCode')}
                    isRequired
                  />
                }
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInputNumber
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="disciplineRate"
                  label="Discipline Rate"
                  placeholder="0.00"
                  min={0.00}
                  step={0.01}
                  precision={2}
                  initialValue={editingVisit?.getDouble('disciplineRate')}
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormTimePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="fromTime"
                  label="Scheduled From Time"
                  placeholder="hh:mm"
                  disabled={editingVisit?.getLong('invoiceId') > 0}
                  initialValue={editingVisit?.getString('fromTimeString') &&
                    moment(editingVisit.getString('fromTimeString'), 'HH:mm')}
                  isRequired
                  onChange={this.onFromTimeChange}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormTimePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="toTime"
                  label="Scheduled To Time"
                  placeholder="hh:mm"
                  disabled={editingVisit?.getLong('invoiceId') > 0}
                  initialValue={editingVisit?.getString('toTimeString') &&
                    moment(editingVisit.getString('toTimeString'), 'HH:mm')}
                  isRequired
                  onChange={this.onToTimeChange}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="billHours"
                  label="Bill Hours"
                  placeholder="hh:mm"
                  max={30}
                  disabled={editingVisit?.getLong('invoiceId') > 0}
                  initialValue={editingVisit?.getString('billHoursString')}
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormSelect2
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="caregiverId"
                  label="Caregiver"
                  placeholder="Select..."
                  options={caregivers}
                  showIdAfterName
                  initialValue={editingVisit?.getLong('caregiverId')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormTimePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="visitIn"
                  label="Confirm Visit - In Time"
                  placeholder="hh:mm"
                  disabled={editingVisit?.getLong('invoiceId') > 0 || isMissedChecked}
                  initialValue={editingVisit?.getString('visitInString') &&
                    moment(editingVisit.getString('visitInString'), 'HH:mm')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormTimePicker
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="visitOut"
                  label="Confirm Visit - Out Time"
                  placeholder="hh:mm"
                  disabled={editingVisit?.getLong('invoiceId') > 0 || isMissedChecked}
                  initialValue={editingVisit?.getString('visitOutString') &&
                    moment(editingVisit.getString('visitOutString'), 'HH:mm')}
                />
              </Col>

              {/* Missed cannot be set during addition. */}
              {!adding &&
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <styledComp.AntdFormItem
                    width="match_parent"
                    height="wrap_content"
                    label="Missed Visit"
                    extra="Please write the missed reason in the remark area."
                  >
                    {getFieldDecorator('missed', {
                      rules: [],
                      initialValue: editingVisit?.getBoolean('missed'),
                      valuePropName: 'checked',
                    })(
                      <Checkbox
                        disabled={editingVisit?.getLong('invoiceId') > 0}
                        onChange={this.onMissedCheckboxChange}
                      >
                        Mark Visit as Missed
                      </Checkbox>,
                    )}
                  </styledComp.AntdFormItem>
                </Col>
              }

              {!adding &&
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormLink
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    label="Invoice Number"
                    href={`/invoices/invoice_details?id=${editingVisit?.getLong('invoiceId')}`}
                    text={editingVisit?.getString('invoiceNumber')}
                  />
                </Col>
              }

              <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                <FormTextArea
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="remark"
                  label="Remark"
                  rows={3}
                  max={2000}
                  initialValue={editingVisit?.getString('remark')}
                />
              </Col>
            </Row>
          </FlexColContainer>

          {/* Footer */}
          <FlexRowContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
            justifyContent="center"
            backgroundColor="containerGrayBackground"
          >
            <CustomButton
              width="wrap_content"
              height="32px"
              margin="0 4px 0 0"
              backgroundColor="blue_steel"
              fontSize="14px"
              type="primary"
              loading={!isSaveButtonEnable}
              htmlType="submit"
            >
              SAVE VISIT
            </CustomButton>

            <styledComp.AntdButton
              width="wrap_content"
              height="32px"
              fontSize="14px"
              type="default"
              onClick={this.onCancelClick}
            >
              CANCEL
            </styledComp.AntdButton>

            {editingVisit && !editingVisit.getLong('invoiceId') &&
              <Popconfirm
                title="Are you sure you want to delete this record?"
                placement="top"
                okText="Confirm"
                cancelText="Cancel"
                onConfirm={() => this.onDeleteClick(editingVisit)}
              >
                <styledComp.AntdButton
                  width="wrap_content"
                  height="32px"
                  margin="0 0 0 64px"
                  fontSize="14px"
                  type="danger"
                  loading={!isDeleteButtonEnable}
                >
                  DELETE VISIT
                </styledComp.AntdButton>
              </Popconfirm>
            }
          </FlexRowContainer>
        </styledComp.AntdForm>
      </FlexColContainer>
    );
  }

  renderContent() {
    const { ...layoutProps } = this.props;
    const { adding, editingVisit } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
        padding="20px 20px"
      >
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          margin="0 0 20px 0"
          justifyContent="space-between"
          alignItems="center"
        >
          <CustomButton
            width="150px"
            height="34px"
            backgroundColor="green_haze"
            fontSize="14px"
            type="primary"
            onClick={this.onAddClick}
          >
            ADD NEW ITEM
            <Icon type="plus" />
          </CustomButton>

          <ChangeHistoriesButton
            width="wrap_content"
            height="wrap_content"
            title="Change History"
            table="ScheduleVisit"
            patientId={Number(this.id)}
          />
        </FlexRowContainer>

        <Calendar
          ref={(ref) => { this.calendar = ref; }}
          width="match_parent"
          height="wrap_content"
          fetchVisits={this.getPatientVisits}
          clickEventHandler={this.onVisitEventClick}
        />

        {(adding || editingVisit) && this.renderAddEditForm()}
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
PatientCalendar.propTypes = propTypes;

export default Form.create()(PatientCalendar);
